import { useCallback, useEffect, useMemo, useRef } from "react";

type Options = {
    ids: string[];
    activeId: string | null;
    onChangeActive: (id: string | null) => void;

    behavior?: ScrollBehavior;
    block?: ScrollLogicalPosition;
    inline?: ScrollLogicalPosition;
};

export function useActiveScrollNavigator({
    ids,
    activeId,
    onChangeActive,
    behavior = "smooth",
    block = "nearest",
    inline = "nearest",
}: Options) {
    const itemRefs = useRef<Record<string, HTMLDivElement | null>>({});

    const activeIndex = useMemo(
        () => (activeId ? ids.findIndex((id) => id === activeId) : -1),
        [ids, activeId],
    );

    const getItemRef = useCallback(
        (id: string) => (el: HTMLDivElement | null) => {
            itemRefs.current[id] = el;
        },
        [],
    );

    const scrollToId = useCallback(
        (id: string | null) => {
            if (!id) return;
            const el = itemRefs.current[id];
            el?.scrollIntoView({ behavior, block, inline });
        },
        [behavior, block, inline],
    );

    useEffect(() => {
        scrollToId(activeId);
    }, [activeId, scrollToId]);

    const selectByOffset = useCallback(
        (offset: number) => {
            if (!ids.length || activeIndex === -1) return;
            const nextIndex = (activeIndex + offset + ids.length) % ids.length;
            const nextId = ids[nextIndex];

            if (!nextId) return;

            scrollToId(nextId);

            onChangeActive(nextId);
        },
        [ids, activeIndex, onChangeActive, scrollToId],
    );

    const onNext = useCallback(() => selectByOffset(1), [selectByOffset]);
    const onPrev = useCallback(() => selectByOffset(-1), [selectByOffset]);
    const scrollToActive = useCallback(
        () => scrollToId(activeId),
        [activeId, scrollToId],
    );

    return {
        activeIndex,
        getItemRef,
        scrollToId,
        onNext,
        onPrev,
        selectByOffset,
        scrollToActive,
    };
}
